/**
 * Merkle Tree Cryptographic Integrity Engine
 *
 * Implements RFC 6962 compliant domain-separated Merkle Trees with
 * BLAKE3 / SHA-512 hashing to safeguard photos and media against
 * bit-flip attacks, silent cloud corruption, and unauthorized data modification.
 */

import { blake3, createBLAKE3, sha512, createSHA512, sha256, createSHA256 } from 'hash-wasm';

export type HashAlgorithm = 'blake3' | 'sha512' | 'sha256';

export interface MerkleLeafItem {
  itemId: string;
  category?: string;
  hash: string; // Blake3 or SHA-512 hash of the content
  size?: number;
  updatedAt?: number;
}

export interface MerkleProofStep {
  position: 'left' | 'right';
  hash: string;
}

export interface MerkleProof {
  itemId: string;
  leafHash: string;
  rootHash: string;
  algorithm: HashAlgorithm;
  proof: MerkleProofStep[];
  leafIndex: number;
  totalLeaves: number;
  verified: boolean;
}

export interface MerkleTreeSnapshot {
  rootHash: string;
  algorithm: HashAlgorithm;
  leafCount: number;
  treeDepth: number;
  leaves: { itemId: string; leafHash: string }[];
  computedAt: number;
}

// Domain separation prefixes (RFC 6962 standard) to prevent second-preimage attacks
const LEAF_PREFIX = '00';
const NODE_PREFIX = '01';
const sharedTextEncoder = new TextEncoder();

/**
 * Compute cryptographic hash of a Uint8Array buffer or string
 */
export const computeBufferHash = async (
  data: Uint8Array | ArrayBuffer | string,
  algorithm: HashAlgorithm = 'blake3',
): Promise<string> => {
  const buffer =
    typeof data === 'string'
      ? sharedTextEncoder.encode(data)
      : data instanceof Uint8Array
        ? data
        : new Uint8Array(data);

  if (buffer.length === 0) {
    if (algorithm === 'blake3') return blake3('');
    if (algorithm === 'sha512') return sha512('');
    return sha256('');
  }

  // Hardware-accelerated Web Crypto for SHA-256 / SHA-512 (zero WASM overhead, native CPU throughput)
  if (
    (algorithm === 'sha256' || algorithm === 'sha512') &&
    typeof crypto !== 'undefined' &&
    crypto.subtle?.digest
  ) {
    try {
      const algoName = algorithm === 'sha512' ? 'SHA-512' : 'SHA-256';
      const digestInput =
        buffer.byteOffset === 0 && buffer.byteLength === buffer.buffer.byteLength
          ? buffer.buffer
          : buffer.buffer.slice(buffer.byteOffset, buffer.byteOffset + buffer.byteLength);
      const hashBuf = await crypto.subtle.digest(algoName, digestInput as any);
      const arr = new Uint8Array(hashBuf);
      let hex = '';
      for (let i = 0; i < arr.length; i++) {
        hex += arr[i].toString(16).padStart(2, '0');
      }
      return hex;
    } catch {
      // Fallback to hash-wasm below
    }
  }

  try {
    if (algorithm === 'blake3') {
      return await blake3(buffer);
    } else if (algorithm === 'sha512') {
      return await sha512(buffer);
    } else {
      return await sha256(buffer);
    }
  } catch {
    // Fallback using Web Crypto API if hash-wasm fails in specific restricted environments
    const algoName = algorithm === 'sha512' ? 'SHA-512' : 'SHA-256';
    const hashBuf = await crypto.subtle.digest(algoName, buffer as any);
    const arr = new Uint8Array(hashBuf);
    let hex = '';
    for (let i = 0; i < arr.length; i++) {
      hex += arr[i].toString(16).padStart(2, '0');
    }
    return hex;
  }
};

/**
 * Compute chunked Blake3 / SHA-512 hash for large files without UI stutter
 */
export const computeFileHashStreaming = async (
  file: File | Blob,
  algorithm: HashAlgorithm = 'blake3',
  chunkSize: number = 2 * 1024 * 1024,
  onProgress?: (percent: number) => void,
): Promise<string> => {
  if (file.size === 0) {
    return computeBufferHash(new Uint8Array(0), algorithm);
  }

  const hasher =
    algorithm === 'blake3'
      ? await createBLAKE3()
      : algorithm === 'sha512'
        ? await createSHA512()
        : await createSHA256();

  hasher.init();
  const totalChunks = Math.ceil(file.size / chunkSize);

  for (let i = 0; i < totalChunks; i++) {
    const start = i * chunkSize;
    const end = Math.min(start + chunkSize, file.size);
    const slice = file.slice(start, end);
    const arrayBuf = await slice.arrayBuffer();
    hasher.update(new Uint8Array(arrayBuf));

    if (onProgress) {
      onProgress(Math.round(((i + 1) / totalChunks) * 100));
    }

    // Yield control briefly to ensure 60fps UI responsiveness
    if (totalChunks > 5) {
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
    }
  }

  return hasher.digest('hex');
};

/**
 * Compute the domain-separated leaf hash for a vault item
 * H_leaf = Hash( 0x00 || itemId || ":" || category || ":" || contentHash || ":" || size )
 */
export const computeLeafHash = async (
  item: MerkleLeafItem,
  algorithm: HashAlgorithm = 'blake3',
): Promise<string> => {
  const leafPayload = `${LEAF_PREFIX}:${item.itemId}:${item.category || 'photo'}:${item.hash}:${item.size || 0}`;
  return computeBufferHash(leafPayload, algorithm);
};

/**
 * Compute the domain-separated parent node hash from left and right children
 * H_parent = Hash( 0x01 || leftChildHash || rightChildHash )
 */
export const computeNodeHash = async (
  left: string,
  right: string,
  algorithm: HashAlgorithm = 'blake3',
): Promise<string> => {
  const nodePayload = `${NODE_PREFIX}:${left}:${right}`;
  return computeBufferHash(nodePayload, algorithm);
};

/**
 * Cryptographic Merkle Tree class
 */
export class MerkleTree {
  private leaves: { item: MerkleLeafItem; leafHash: string }[] = [];
  private layers: string[][] = [];
  private algorithm: HashAlgorithm;
  private root: string = '';

  constructor(algorithm: HashAlgorithm = 'blake3') {
    this.algorithm = algorithm;
  }

  /**
   * Build the Merkle Tree from an array of vault items.
   * Items are deterministically sorted by itemId to ensure invariant roots.
   */
  async build(items: MerkleLeafItem[]): Promise<string> {
    if (!items || items.length === 0) {
      this.leaves = [];
      this.layers = [[]];
      this.root = await computeBufferHash('empty_vault_merkle_root', this.algorithm);
      return this.root;
    }

    // Deterministic sorting by itemId (lexicographical)
    const sortedItems = [...items].sort((a, b) => a.itemId.localeCompare(b.itemId));

    // Generate leaf hashes in parallel
    const leafNodes = await Promise.all(
      sortedItems.map(async (item) => {
        const leafHash = await computeLeafHash(item, this.algorithm);
        return { item, leafHash };
      }),
    );

    this.leaves = leafNodes;
    let currentLayer = leafNodes.map((l) => l.leafHash);
    this.layers = [currentLayer];

    // Iteratively build layers up to root in parallel pairs
    while (currentLayer.length > 1) {
      const pairs: Array<[string, string]> = [];
      for (let i = 0; i < currentLayer.length; i += 2) {
        const left = currentLayer[i];
        // If odd number of nodes, pair the last node with itself
        const right = i + 1 < currentLayer.length ? currentLayer[i + 1] : left;
        pairs.push([left, right]);
      }
      const nextLayer = await Promise.all(
        pairs.map(([left, right]) => computeNodeHash(left, right, this.algorithm)),
      );
      this.layers.push(nextLayer);
      currentLayer = nextLayer;
    }

    this.root =
      currentLayer[0] || (await computeBufferHash('empty_vault_merkle_root', this.algorithm));
    return this.root;
  }

  /**
   * Get the current Merkle Root hash
   */
  getRoot(): string {
    return this.root;
  }

  /**
   * Get total leaves count
   */
  getLeafCount(): number {
    return this.leaves.length;
  }

  /**
   * Get tree depth
   */
  getDepth(): number {
    return this.layers.length;
  }

  /**
   * Generate an inclusion proof (audit path) for a specific item
   */
  generateProof(itemId: string): MerkleProof | null {
    const leafIndex = this.leaves.findIndex((l) => l.item.itemId === itemId);
    if (leafIndex === -1) return null;

    const proof: MerkleProofStep[] = [];
    let currentIndex = leafIndex;

    for (let layerIdx = 0; layerIdx < this.layers.length - 1; layerIdx++) {
      const layer = this.layers[layerIdx];
      const isRightChild = currentIndex % 2 === 1;
      const siblingIndex = isRightChild ? currentIndex - 1 : currentIndex + 1;

      if (siblingIndex < layer.length) {
        proof.push({
          position: isRightChild ? 'left' : 'right',
          hash: layer[siblingIndex],
        });
      } else {
        // Odd node paired with itself
        proof.push({
          position: 'right',
          hash: layer[currentIndex],
        });
      }

      currentIndex = Math.floor(currentIndex / 2);
    }

    return {
      itemId,
      leafHash: this.leaves[leafIndex].leafHash,
      rootHash: this.root,
      algorithm: this.algorithm,
      proof,
      leafIndex,
      totalLeaves: this.leaves.length,
      verified: true,
    };
  }

  /**
   * Verify an inclusion proof against a root hash
   */
  static async verifyProof(
    leafHash: string,
    proof: MerkleProofStep[],
    expectedRoot: string,
    algorithm: HashAlgorithm = 'blake3',
  ): Promise<boolean> {
    if (!leafHash || !expectedRoot) return false;
    let currentHash = leafHash;

    for (const step of proof) {
      if (step.position === 'left') {
        currentHash = await computeNodeHash(step.hash, currentHash, algorithm);
      } else {
        currentHash = await computeNodeHash(currentHash, step.hash, algorithm);
      }
    }

    return currentHash === expectedRoot;
  }

  /**
   * Export snapshot of the tree state
   */
  getSnapshot(): MerkleTreeSnapshot {
    return {
      rootHash: this.root,
      algorithm: this.algorithm,
      leafCount: this.leaves.length,
      treeDepth: this.layers.length,
      leaves: this.leaves.map((l) => ({ itemId: l.item.itemId, leafHash: l.leafHash })),
      computedAt: Date.now(),
    };
  }
}

/**
 * Bit-flip detection and simulation helper
 */
export const detectBitFlipCorruption = async (
  originalBuffer: Uint8Array,
  expectedHash: string,
  algorithm: HashAlgorithm = 'blake3',
): Promise<{
  isCorrupted: boolean;
  actualHash: string;
  expectedHash: string;
  match: boolean;
}> => {
  const actualHash = await computeBufferHash(originalBuffer, algorithm);
  const isCorrupted = actualHash.toLowerCase() !== expectedHash.toLowerCase();

  return {
    isCorrupted,
    actualHash,
    expectedHash,
    match: !isCorrupted,
  };
};

/**
 * Utility to simulate a 1-bit flip in binary data (for testing & live security demos)
 */
export const simulateBitFlip = (
  data: Uint8Array,
  bytePosition = 0,
  bitPosition = 0,
): Uint8Array => {
  const tampered = new Uint8Array(data);
  if (tampered.length === 0) return tampered;
  const targetByte = Math.min(Math.max(0, bytePosition), tampered.length - 1);
  const bitMask = 1 << (bitPosition % 8);
  tampered[targetByte] = tampered[targetByte] ^ bitMask;
  return tampered;
};
